import VectorLayer from 'ol/layer/Vector.js'
import GeoJSON from 'ol/format/GeoJSON.js'
import { transformExtent } from 'ol/proj.js'
import { fetchWithTimeout, parseXmlOrThrow, checkServiceException, extractBBox } from './ogcXml'
import { createProgressVectorSource } from './vectorProgressSource'

// GeoServer (implementación de referencia de este proyecto) interpreta bbox=minx,miny,maxx,maxy sin
// sufijo de CRS como lon/lat — evita el problema de orden de ejes de "urn:ogc:def:crs:EPSG::4326".
function bboxParam(extent, projection) {
  const [minLon, minLat, maxLon, maxLat] = transformExtent(extent, projection, 'EPSG:4326')
  return `${minLon},${minLat},${maxLon},${maxLat}`
}

const CAPS_TIMEOUT_MS = 10000
const WFS_HIT_TOLERANCE_PX = 8 // radio en píxeles usado para hit-test de features WFS ya cargados

export const wfsConnector = {
  id: 'wfs-external',
  serviceType: 'WFS',
  label: 'WFS externo',

  matchesUrl() {
    return false
  },

  async detect(baseUrl) {
    const capsUrl = `${baseUrl}?service=WFS&request=GetCapabilities&version=2.0.0`
    const res = await fetchWithTimeout(capsUrl, CAPS_TIMEOUT_MS)
    const txt = await res.text()
    const xml = parseXmlOrThrow(txt)
    checkServiceException(xml)

    const layers = [...xml.querySelectorAll('FeatureType > Name')].map(n => ({
      name: n.textContent,
      title: n.parentElement.querySelector(':scope > Title')?.textContent || n.textContent,
      bbox: extractBBox(n.parentElement, true),
      meta: {}
    }))
    if (!layers.length) {
      throw new Error('No se encontraron capas (revisa la URL o los permisos CORS del servidor).')
    }
    return layers
  },

  createOlLayer(layerConfig) {
    // Capa WFS agregada por URL en sesión: features vectoriales cargadas vía GetFeature
    // (WFS 2.0.0, mismo formato de salida que ya usa este proyecto para WFS de GeoServer),
    // filtrados por la extensión visible del mapa (strategy: bbox) — no se trae la capa completa.
    // Sin estilo custom: igual que las capas KML/SHP existentes, se usa el estilo por defecto de OL.
    const baseUrl = layerConfig.provider.url
    const layerName = layerConfig.provider.layerName
    const featureBaseUrl = `${baseUrl}?service=WFS&version=2.0.0&request=GetFeature`
      + `&typeNames=${encodeURIComponent(layerName)}&outputFormat=application/json`

    const source = createProgressVectorSource({
      layerId: layerConfig.id,
      layerName: layerConfig.name,
      format: new GeoJSON(),
      buildSingleShotUrl: ({ extent, projection }) => `${featureBaseUrl}&bbox=${bboxParam(extent, projection)}`,

      // Conteo previo vía resultType=hits (WFS 2.0), acotado a la extensión visible. Si el servidor
      // no lo soporta o no informa numberMatched, getTotalCount devuelve null y se cae al fallback.
      async getTotalCount({ extent, projection, signal }) {
        const hitsUrl = `${baseUrl}?service=WFS&version=2.0.0&request=GetFeature`
          + `&typeNames=${encodeURIComponent(layerName)}&resultType=hits&bbox=${bboxParam(extent, projection)}`
        const res = await fetchWithTimeout(hitsUrl, CAPS_TIMEOUT_MS, signal)
        const txt = await res.text()
        const xml = parseXmlOrThrow(txt)
        checkServiceException(xml)
        const numberMatched = parseInt(xml.documentElement?.getAttribute('numberMatched'), 10)
        return Number.isFinite(numberMatched) ? numberMatched : null
      },

      async fetchPage({ extent, projection, offset, pageSize, signal }) {
        const pageUrl = `${featureBaseUrl}&bbox=${bboxParam(extent, projection)}&startIndex=${offset}&count=${pageSize}`
        const res = await fetch(pageUrl, { signal })
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
        const data = await res.json()
        return data.features || []
      }
    })

    return new VectorLayer({
      source,
      opacity: layerConfig.opacity !== undefined ? layerConfig.opacity : 1,
      zIndex: 10
    })
  },

  queryFeatures({ layer, pixel, map, olLayer }) {
    if (!pixel || !map || !olLayer) return null

    const geoJSONFormat = new GeoJSON()
    const hitFeatures = []
    map.forEachFeatureAtPixel(pixel, (feature) => {
      hitFeatures.push(geoJSONFormat.writeFeatureObject(feature))
    }, {
      hitTolerance: WFS_HIT_TOLERANCE_PX,
      layerFilter: (l) => l === olLayer
    })

    return {
      layerName: layer.name,
      features: hitFeatures
    }
  }
}
